import React from 'react';
import styles from './Menu.module.scss';
import { MenuShape } from './Menu';
import { BodyText, Div, Flex, Element, Icon, Link, LinkAdapter, MarginSpace } from '@gaiads/telia-react-component-library';

export interface MenuItemShape {
  id: string;
  text: string;
  url?: string;
  menuItems?: MenuItemShape[];
  onMenuItemClick?: (e: React.MouseEvent<HTMLDivElement>, id: string, level: number, locked: boolean) => void;
  forMobile?: boolean;
  level: number;
  isOpen?: boolean;
  tabbable?: boolean;
  linkAdapter?: LinkAdapter;
  locked?: boolean;
  showLockIcon?: boolean;
}

interface MenuItemProps extends MenuItemShape {
  menuComponent: React.FunctionComponent<MenuShape>;
  active?: boolean;
}

const MenuItem: React.FunctionComponent<MenuItemProps> = ({
  text,
  url,
  menuItems,
  onMenuItemClick,
  id,
  forMobile,
  level = 1,
  isOpen = false,
  tabbable = true,
  linkAdapter,
  locked = false,
  showLockIcon = true,
  menuComponent,
  active,
}: MenuItemProps) => {
  const Menu = menuComponent;
  const hasSubItems = !!menuItems?.length;

  const dropdownIcon = (
    <Div className={[styles.menu_dropdownIcon, styles[`menu_dropdownIcon__level${level}`]]}>
      <svg viewBox="0 0 16 9" height="10" width="10">
        <path d="M8.077 7.088L14.37.633c.456-.456.786-.731 1.35-.18.564.55.174.98-.282 1.437L8.903 8.424a1.168 1.168 0 0 1-1.652 0L.717 1.89C.089 1.262-.301.872.297.274c.598-.598 1.01-.097 1.466.36l6.314 6.454z"></path>
      </svg>
    </Div>
  );

  const lockShown = Boolean(locked && showLockIcon);

  return hasSubItems ? (
    <Div
      className={[styles.menu_item, styles[`menu_item__level${level}`], { [styles.menu_item__selected]: isOpen }]}
      data-menu={id}
      data-open={`${isOpen}`}
      onClick={event => onMenuItemClick && onMenuItemClick(event, id, level, false)}
    >
      {forMobile ? (
        <Element
          data-testid="menu_item_tabbable"
          block={true}
          tagName="button"
          className={[styles.menu_itemButton, styles[`menu_itemButton__level${level}`]]}
          tabIndex={tabbable ? 0 : -1}
        >
          <MarginSpace>
            <Div className={[styles.menu_itemText, styles[`menu_itemText__level${level}`]]}>
              {text}

              {dropdownIcon}
            </Div>
          </MarginSpace>
        </Element>
      ) : (
        <BodyText
          data-testid="menu_item_tabbable"
          tagName="button"
          className={[styles.menu_itemText, styles[`menu_itemText__level${level}`], { [styles.menu_item__active]: active }]}
          tabIndex={tabbable ? 0 : -1}
        >
          {text}

          {dropdownIcon}
        </BodyText>
      )}

      <Menu
        data-testid="menu"
        menuItems={menuItems}
        level={level + 1}
        onMenuItemClick={onMenuItemClick}
        forMobile={forMobile}
        linkAdapter={linkAdapter}
        isOpen={isOpen}
      />
    </Div>
  ) : (
    <Div
      className={[styles.menu_item, styles[`menu_item__level${level}`]]}
      data-menuitem={id}
      onClick={event => {
        /* istanbul ignore else */
        if (locked) {
          event.preventDefault();
          /* istanbul ignore else */
          if (url) {
            localStorage.setItem('redirectLockedUrl', url);
          }
        }
        onMenuItemClick?.(event, id, level, locked);
      }}
    >
      {forMobile ? (
        <Link
          data-testid="menu_item_tabbable"
          color="black"
          hoverColor="black"
          to={url}
          linkAdapter={linkAdapter}
          className={[styles.menu_itemText, styles[`menu_itemText__level${level}`], { [styles.menu_item__active]: active }]}
          tabIndex={tabbable ? 0 : -1}
          occupyHorizontalSpace={true}
        >
          <MarginSpace>
            <Flex spaceBetween centeredVertically>
              <BodyText tagName="span">{text}</BodyText>
              <Icon name="lock-closed" size="sm" shown={lockShown} data-testid="menu_item_lock_icon" data-shown={`${lockShown}`} />
            </Flex>
          </MarginSpace>
        </Link>
      ) : (
        <Link
          data-testid="menu_item_tabbable"
          color="black"
          hoverColor="black"
          to={url}
          linkAdapter={linkAdapter}
          className={[styles.menu_itemText, styles[`menu_itemText__level${level}`], { [styles.menu_item__active]: active }]}
          tabIndex={tabbable ? 0 : -1}
          occupyHorizontalSpace={true}
        >
          <Flex spaceBetween centeredVertically>
            <BodyText tagName="span">{text}</BodyText>
            <Icon name="lock-closed" size="sm" shown={lockShown} data-testid="menu_item_lock_icon" data-shown={`${lockShown}`} />
          </Flex>
        </Link>
      )}
    </Div>
  );
};

export default MenuItem;
